// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import type { BankUploadTemplateEntity } from '@client/banks/utils/download-bank-upload-template';
import { isDefined, isNullOrUndefined, isUndefined } from '@shared/utils';
import * as XLSX from 'xlsx';
import {
  BANK_UPLOAD_ENTITIES_REQUIRING_ACCOUNT_IBAN,
  isBankUploadIbanInAccountSet,
} from './bank-upload-account-iban-set';
import {
  getBankUploadExcelFileSizeError,
  getBankUploadExcelRowCountError,
} from './bank-upload-excel-limits';
import {
  buildRawRowRecord,
  mapAmortizationRaw,
  mapEntityRow,
} from './map-excel-row-to-domain';
import { mapZodIssueToParseError } from './map-zod-issue-to-parse-error';
import { isExcelRowBlank } from './normalize-excel-cell';
import parserMapping from './parser-mapping.json';
import type {
  BankUploadParseError,
  BankUploadParseResult,
  BankUploadTemplateLocale,
  ParseBankUploadWorkbookOptions,
  ParserMappingDocument,
} from './types';

const MAPPING = parserMapping as ParserMappingDocument;

type SheetParseAccumulator = {
  rows: unknown[];
  errors: BankUploadParseError[];
  validCount: number;
  invalidCount: number;
};

const emptySheetParse = (): SheetParseAccumulator => ({
  rows: [],
  errors: [],
  validCount: 0,
  invalidCount: 0,
});

const cellToTrimmedString = (cell: unknown): string =>
  isNullOrUndefined(cell) ? '' : String(cell).trim();

const readWorkbookFromFile = async (file: File): Promise<XLSX.WorkBook> => {
  const buffer = await file.arrayBuffer();

  return XLSX.read(buffer, { type: 'array', cellDates: true });
};

const readMetaMap = (workbook: XLSX.WorkBook): Record<string, string> | null => {
  const sheet = workbook.Sheets[MAPPING.meta.sheet];

  if (isUndefined(sheet)) {
    return null;
  }

  const matrix = XLSX.utils.sheet_to_json<unknown[]>(sheet, {
    header: 1,
    raw: false,
    defval: null,
    blankrows: false,
  });

  return matrix.reduce<Record<string, string>>((meta, row) => {
    const key = row?.[0];

    const value = row?.[1];

    if (isNullOrUndefined(key) || String(key).trim() === '') {
      return meta;
    }

    return {
      ...meta,
      [String(key).trim()]: cellToTrimmedString(value),
    };
  }, {});
};

const validateMeta = (
  meta: Record<string, string> | null,
  expectedEntity: BankUploadTemplateEntity,
): { locale: BankUploadTemplateLocale } | { errors: BankUploadParseError[] } => {
  if (isNullOrUndefined(meta)) {
    return {
      errors: [
        {
          code: 'missing_meta',
          sheet: MAPPING.meta.sheet,
        },
      ],
    };
  }

  for (const key of MAPPING.meta.requiredKeys) {
    if (isNullOrUndefined(meta[key]) || meta[key] === '') {
      return {
        errors: [
          {
            code: 'invalid_template',
            sheet: MAPPING.meta.sheet,
            field: key,
          },
        ],
      };
    }
  }

  if (meta.template_id !== MAPPING.meta.templateId) {
    return {
      errors: [
        {
          code: 'invalid_template',
          sheet: MAPPING.meta.sheet,
          field: 'template_id',
        },
      ],
    };
  }

  if (meta.official !== 'true') {
    return {
      errors: [
        {
          code: 'invalid_template',
          sheet: MAPPING.meta.sheet,
          field: 'official',
        },
      ],
    };
  }

  if (meta.entity !== expectedEntity) {
    return {
      errors: [
        {
          code: 'entity_mismatch',
          sheet: MAPPING.meta.sheet,
          field: 'entity',
          values: {
            actual: meta.entity,
            expected: expectedEntity,
          },
        },
      ],
    };
  }

  if (meta.schema_version !== MAPPING.meta.schemaVersion) {
    return {
      errors: [
        {
          code: 'unsupported_schema',
          sheet: MAPPING.meta.sheet,
          field: 'schema_version',
          values: { version: meta.schema_version },
        },
      ],
    };
  }

  const locale = meta.locale as BankUploadTemplateLocale;

  if (locale !== 'en' && locale !== 'es') {
    return {
      errors: [
        {
          code: 'invalid_template',
          sheet: MAPPING.meta.sheet,
          field: 'locale',
        },
      ],
    };
  }

  return { locale };
};

const readSheetMatrix = (
  workbook: XLSX.WorkBook,
  sheetName: string,
): unknown[][] | null => {
  const sheet = workbook.Sheets[sheetName];

  if (isUndefined(sheet)) {
    return null;
  }

  return XLSX.utils.sheet_to_json<unknown[]>(sheet, {
    header: 1,
    raw: true,
    defval: null,
    blankrows: false,
  });
};

const listNonBlankDataRows = (
  matrix: unknown[][],
  dataStartIndex: number,
): Array<{ cells: unknown[]; excelRow: number }> =>
  matrix.slice(dataStartIndex).flatMap((row, offset) => {
    const cells = row ?? [];

    if (isExcelRowBlank(cells)) {
      return [];
    }

    return [{ cells, excelRow: dataStartIndex + offset + 1 }];
  });

const parseEntitySheet = (
  workbook: XLSX.WorkBook,
  entity: BankUploadTemplateEntity,
  locale: BankUploadTemplateLocale,
  options: ParseBankUploadWorkbookOptions = {},
): SheetParseAccumulator => {
  const entityMapping = MAPPING.entities[entity];

  const matrix = readSheetMatrix(workbook, entityMapping.sheet);

  if (isNullOrUndefined(matrix)) {
    return {
      ...emptySheetParse(),
      errors: [
        {
          code: 'missing_sheet',
          sheet: entityMapping.sheet,
          values: { sheet: entityMapping.sheet },
        },
      ],
    };
  }

  const headerIndex = entityMapping.headerRow - 1;

  const dataStartIndex = entityMapping.dataStartRow - 1;

  const keyRow = matrix[headerIndex] ?? [];

  const keys = keyRow.map(cellToTrimmedString);

  const expectedKeys = entityMapping.fields.map(field => field.key);

  const missingKeys = expectedKeys.filter(key => !keys.includes(key));

  if (missingKeys.length > 0) {
    return {
      ...emptySheetParse(),
      errors: [
        {
          code: 'invalid_headers',
          sheet: entityMapping.sheet,
          row: entityMapping.headerRow,
          values: { keys: missingKeys.join(', ') },
        },
      ],
    };
  }

  const dataRows = listNonBlankDataRows(matrix, dataStartIndex);

  const rowLimitError = getBankUploadExcelRowCountError(
    dataRows.length,
    entityMapping.sheet,
  );

  if (isDefined(rowLimitError)) {
    return {
      ...emptySheetParse(),
      errors: [rowLimitError],
    };
  }

  const allowedAccountIbans = options.allowedAccountIbans;

  const mustMatchAccountIban =
    BANK_UPLOAD_ENTITIES_REQUIRING_ACCOUNT_IBAN.has(entity) &&
    isDefined(allowedAccountIbans);

  return dataRows.reduce<SheetParseAccumulator>((acc, { cells, excelRow }) => {
    const raw = buildRawRowRecord(
      keys,
      cells,
      entityMapping.fields,
      MAPPING,
      locale,
    );

    const parsed = mapEntityRow(entity, raw);

    if (!parsed.success) {
      return {
        ...acc,
        invalidCount: acc.invalidCount + 1,
        errors: [
          ...acc.errors,
          ...parsed.error.issues.map(issue =>
            mapZodIssueToParseError(issue, entity, excelRow, entityMapping.sheet),
          ),
        ],
      };
    }

    const rowIban = (parsed.data as { iban?: unknown }).iban;
    const hasLinkedIban = typeof rowIban === 'string' && rowIban.trim() !== '';

    if (
      mustMatchAccountIban &&
      hasLinkedIban &&
      !isBankUploadIbanInAccountSet(rowIban, allowedAccountIbans)
    ) {
      return {
        ...acc,
        invalidCount: acc.invalidCount + 1,
        errors: [
          ...acc.errors,
          {
            code: 'row_invalid',
            issueKind: 'unknown_account',
            entity,
            sheet: entityMapping.sheet,
            row: excelRow,
            field: 'iban',
          },
        ],
      };
    }

    return {
      ...acc,
      validCount: acc.validCount + 1,
      rows: [...acc.rows, parsed.data],
    };
  }, emptySheetParse());
};

const attachDebtAmortizations = (
  workbook: XLSX.WorkBook,
  debts: Array<Record<string, unknown>>,
  locale: BankUploadTemplateLocale,
  entity: BankUploadTemplateEntity,
): {
  rows: Array<Record<string, unknown>>;
  errors: BankUploadParseError[];
} => {
  const amortMapping = MAPPING.entities.debt_amortizations;

  if (isUndefined(amortMapping)) {
    return { rows: debts, errors: [] };
  }

  const matrix = readSheetMatrix(workbook, amortMapping.sheet);

  if (isNullOrUndefined(matrix)) {
    return { rows: debts, errors: [] };
  }

  const headerIndex = amortMapping.headerRow - 1;

  const dataStartIndex = amortMapping.dataStartRow - 1;

  const keys = (matrix[headerIndex] ?? []).map(cellToTrimmedString);

  const dataRows = listNonBlankDataRows(matrix, dataStartIndex);

  const amortRowLimitError = getBankUploadExcelRowCountError(
    dataRows.length,
    amortMapping.sheet,
  );

  if (isDefined(amortRowLimitError)) {
    return { rows: debts, errors: [amortRowLimitError] };
  }

  const { byLink, errors } = dataRows.reduce<{
    byLink: ReadonlyMap<string, readonly unknown[]>;
    errors: BankUploadParseError[];
  }>(
    (acc, { cells, excelRow }) => {
      const raw = buildRawRowRecord(
        keys,
        cells,
        amortMapping.fields,
        MAPPING,
        locale,
      );

      const mapped = mapAmortizationRaw(raw);

      if (!mapped.ok) {
        return {
          ...acc,
          errors: [
            ...acc.errors,
            ...mapped.issues.map(issue =>
              mapZodIssueToParseError(issue, entity, excelRow, amortMapping.sheet),
            ),
          ],
        };
      }

      const linkKey = `${mapped.link.debtIban}::${mapped.link.contractNumber}`;

      const existing = acc.byLink.get(linkKey) ?? [];

      return {
        ...acc,
        byLink: new Map(acc.byLink).set(linkKey, [...existing, mapped.data]),
      };
    },
    { byLink: new Map(), errors: [] },
  );

  const rows = debts.map(debt => {
    const iban = String(debt.iban ?? '');

    const contractNumber = String(debt.contractNumber ?? '');

    if (iban === '' || contractNumber === '') {
      return debt;
    }

    const amortizations = byLink.get(`${iban}::${contractNumber}`);

    if (isUndefined(amortizations) || amortizations.length === 0) {
      return debt;
    }

    return {
      ...debt,
      amortizations,
    };
  });

  return { rows, errors };
};

/**
 * Parse and validate an official bank-upload workbook for one entity. Does not
 * mutate wizard form state — caller decides how to apply `rows`.
 */
export const parseBankUploadWorkbook = async (
  file: File,
  expectedEntity: BankUploadTemplateEntity,
  options: ParseBankUploadWorkbookOptions = {},
): Promise<BankUploadParseResult> => {
  const base: BankUploadParseResult = {
    ok: false,
    entity: expectedEntity,
    locale: null,
    rows: [],
    validCount: 0,
    invalidCount: 0,
    errors: [],
  };

  try {
    const fileSizeError = getBankUploadExcelFileSizeError(file);

    if (isDefined(fileSizeError)) {
      return { ...base, errors: [fileSizeError] };
    }

    const workbook = await readWorkbookFromFile(file);

    const metaResult = validateMeta(readMetaMap(workbook), expectedEntity);

    if ('errors' in metaResult) {
      return { ...base, errors: metaResult.errors };
    }

    const { locale } = metaResult;

    const sheetResult = parseEntitySheet(workbook, expectedEntity, locale, options);

    if (
      sheetResult.errors.some(
        error =>
          error.code === 'missing_sheet' ||
          error.code === 'invalid_headers' ||
          error.code === 'too_many_rows',
      )
    ) {
      return {
        ...base,
        locale,
        errors: sheetResult.errors,
      };
    }

    const amortResult =
      expectedEntity === 'debts'
        ? attachDebtAmortizations(
            workbook,
            sheetResult.rows as Array<Record<string, unknown>>,
            locale,
            expectedEntity,
          )
        : {
            rows: sheetResult.rows as Array<Record<string, unknown>>,
            errors: [] as BankUploadParseError[],
          };

    if (amortResult.errors.some(error => error.code === 'too_many_rows')) {
      return {
        ...base,
        locale,
        errors: amortResult.errors.filter(error => error.code === 'too_many_rows'),
      };
    }

    const errors = [...sheetResult.errors, ...amortResult.errors];

    const hasBlockingErrors =
      sheetResult.validCount === 0 || sheetResult.invalidCount > 0;

    if (sheetResult.validCount === 0 && sheetResult.invalidCount === 0) {
      return {
        ...base,
        locale,
        errors: [
          {
            code: 'empty_data',
            sheet: MAPPING.entities[expectedEntity].sheet,
          },
          ...errors,
        ],
      };
    }

    return {
      ok: !hasBlockingErrors && amortResult.errors.length === 0,
      entity: expectedEntity,
      locale,
      rows: amortResult.rows,
      validCount: sheetResult.validCount,
      invalidCount: sheetResult.invalidCount,
      errors,
    };
  } catch {
    return {
      ...base,
      errors: [{ code: 'invalid_file' }],
    };
  }
};
